import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Save, Check, Plus, Trash2, Star } from "lucide-react";
import BilingualField from "../BilingualField";

export default function PricingManager() {
  const [data, setData] = useState({ overline: { fr: "", en: "" }, title: { fr: "", en: "" }, description: { fr: "", en: "" }, footer_note: { fr: "", en: "" }, packs: [] });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/content/pricing").then(({ data: d }) => setData(d)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/content/pricing", { data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally {
      setLoading(false);
    }
  };

  const updatePack = (i, key, value) => {
    const packs = [...(data.packs || [])];
    packs[i] = { ...packs[i], [key]: value };
    setData({ ...data, packs });
  };

  const addPack = () => {
    const packs = [...(data.packs || []), {
      id: `pack-${Date.now()}`,
      name: { fr: "", en: "" }, price: "0", currency: "€",
      duration: { fr: "", en: "" }, photos: { fr: "", en: "" },
      description: { fr: "", en: "" }, features: [], popular: false
    }];
    setData({ ...data, packs });
  };

  const removePack = (i) => {
    if (!window.confirm("Supprimer ce pack ?")) return;
    const packs = (data.packs || []).filter((_, idx) => idx !== i);
    setData({ ...data, packs });
  };

  const updateFeature = (packIdx, fIdx, value) => {
    const packs = [...(data.packs || [])];
    const features = [...(packs[packIdx].features || [])];
    features[fIdx] = { ...(typeof value === "object" && value !== null ? value : {}), ...value };
    packs[packIdx] = { ...packs[packIdx], features };
    setData({ ...data, packs });
  };

  const makeFeatureKey = () => `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const addFeature = (packIdx) => {
    const packs = [...(data.packs || [])];
    packs[packIdx] = { ...packs[packIdx], features: [...(packs[packIdx].features || []), { _key: makeFeatureKey(), fr: "", en: "" }] };
    setData({ ...data, packs });
  };

  const removeFeature = (packIdx, fIdx) => {
    const packs = [...(data.packs || [])];
    packs[packIdx] = { ...packs[packIdx], features: (packs[packIdx].features || []).filter((_, i) => i !== fIdx) };
    setData({ ...data, packs });
  };

  return (
    <div data-testid="pricing-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl">Tarifs</h1>
          <p className="text-[#4A3F35] mt-2">Packs proposés sur la page <strong>/tarifs</strong>.</p>
        </div>
        <button onClick={save} disabled={loading} className="btn-primary disabled:opacity-50" data-testid="pricing-save">
          {saved ? <><Check size={16} /> Enregistré</> : <><Save size={16} /> Enregistrer</>}
        </button>
      </div>

      <div className="bg-[#FDFBF7] rounded-2xl p-8 border border-[#2D2A26]/10 space-y-5 mb-6">
        <h3 className="font-serif text-xl">En-tête de la page</h3>
        <BilingualField label="overline" value={data.overline} onChange={(v) => setData({ ...data, overline: v })} testid="pricing-overline" />
        <BilingualField label="title" value={data.title} onChange={(v) => setData({ ...data, title: v })} testid="pricing-title" />
        <BilingualField label="description" value={data.description} onChange={(v) => setData({ ...data, description: v })} type="textarea" testid="pricing-desc" />
        <BilingualField label="footer_note (note en bas de page)" value={data.footer_note} onChange={(v) => setData({ ...data, footer_note: v })} type="textarea" testid="pricing-footer-note" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-2xl">Packs ({(data.packs || []).length})</h3>
        <button onClick={addPack} className="btn-ghost !py-2 !px-4 text-xs" data-testid="add-pack"><Plus size={14} /> Ajouter un pack</button>
      </div>

      <div className="space-y-5">
        {(data.packs || []).map((p, i) => (
          <div key={p.id || i} className="bg-[#FDFBF7] rounded-2xl p-6 border border-[#2D2A26]/10" data-testid={`pack-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h4 className="font-serif text-xl">{p.name?.fr || `Pack ${i + 1}`}</h4>
                {p.popular && <span className="text-xs bg-[#C87A5E] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Star size={10} /> Populaire</span>}
              </div>
              <button onClick={() => removePack(i)} className="text-red-700 inline-flex items-center gap-1 text-sm"><Trash2 size={14} /> Supprimer</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <BilingualField label="name (nom du pack)" value={p.name} onChange={(v) => updatePack(i, "name", v)} testid={`pack-${i}-name`} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="price" type="number" value={p.price} onChange={(v) => updatePack(i, "price", v)} />
                <Input label="currency" value={p.currency} onChange={(v) => updatePack(i, "currency", v)} />
              </div>
              <BilingualField label="duration (durée)" value={p.duration} onChange={(v) => updatePack(i, "duration", v)} testid={`pack-${i}-duration`} />
              <BilingualField label="photos (ex: '30 photos retouchées')" value={p.photos} onChange={(v) => updatePack(i, "photos", v)} testid={`pack-${i}-photos`} />
            </div>
            <div className="mt-4">
              <BilingualField label="description courte" value={p.description} onChange={(v) => updatePack(i, "description", v)} type="textarea" testid={`pack-${i}-desc`} />
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm">
              <input type="checkbox" checked={!!p.popular} onChange={(e) => updatePack(i, "popular", e.target.checked)} data-testid={`pack-${i}-popular`} />
              Badge "Le plus populaire"
            </label>

            <div className="mt-5 pt-5 border-t border-[#2D2A26]/10">
              <div className="flex items-center justify-between mb-3">
                <label className="overline">Prestations incluses</label>
                <button onClick={() => addFeature(i)} className="text-xs text-[#C87A5E] inline-flex items-center gap-1"><Plus size={11} /> Ajouter</button>
              </div>
              <div className="space-y-3">
                {(p.features || []).map((f, fi) => {
                  const featureKey = f._key || `${p.id || i}-feat-${fi}-${f.fr || f.en || ""}`;
                  return (
                  <div key={featureKey} className="flex gap-2 items-start">
                    <div className="flex-1"><BilingualField label={`feature #${fi + 1}`} value={f} onChange={(v) => updateFeature(i, fi, v)} testid={`pack-${i}-feat-${fi}`} /></div>
                    <button onClick={() => removeFeature(i, fi)} className="text-red-700 mt-7"><Trash2 size={14} /></button>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm focus:border-[#C87A5E] outline-none" />
    </div>
  );
}
